/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from "three";
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { extrudeSettings, roofCutShape, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../utils/Functions';

import { CSG } from '../../../../../assets/libs/csg-lib';
import { MaterialInnerRoof, MaterialOuterRoof, MaterialRoofRidge } from '../../../CustomMaterial';

const trimThickness = 0.2;
const ridgeThickness = 0.022;
const roofThickness = 0.005;
const roofOutlineThickness = 0.05;

const trimOffsetHeightFunc = (p_r_p) => { return p_r_p / 12 * trimThickness }

const rotateAroundCustomAxis = (direction, mesh, angle, trimOffsetHeight) => {

    const rightLeanPoint1 = new THREE.Vector3((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, -params.p_d / 2); 
    const rightLeanPoint2 = new THREE.Vector3((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, params.p_d / 2);
    const rightLeanAxisVector = new THREE.Vector3().subVectors(rightLeanPoint2, rightLeanPoint1).normalize();
    const rightLeanPivotPoint  = new THREE.Vector3((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, 0) 
    
    const leftLeanPoint1 = new THREE.Vector3(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, -params.p_d / 2); 
    const leftLeanPoint2 = new THREE.Vector3(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, params.p_d / 2);
    const leftLeanAxisVector = new THREE.Vector3().subVectors(leftLeanPoint2, leftLeanPoint1).normalize();
    const leftLeanPivotPoint  = new THREE.Vector3(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, 0) 

    let pivotPoint, axis;
    if (direction === 'left') {
        pivotPoint = leftLeanPivotPoint;
        axis = leftLeanAxisVector
    } else {
        pivotPoint = rightLeanPivotPoint;
        axis = rightLeanAxisVector
    }
    
    // Create translation matrices
    const translationMatrix1 = new THREE.Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
    const translationMatrix2 = new THREE.Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);

    // Create rotation matrix
    const rotationMatrix = new THREE.Matrix4().makeRotationAxis(axis, angle);

    // Apply the transformation: translate to pivot, rotate, then translate back
    mesh.geometry.applyMatrix4(translationMatrix1);
    mesh.geometry.applyMatrix4(rotationMatrix);
    mesh.geometry.applyMatrix4(translationMatrix2);

    // Update the mesh's world matrix
    mesh.updateMatrixWorld();
};

const RoofModel = () => {
    const { gl } = useThree();
    // const baseTexture = useLoader(THREE.TextureLoader, TrimTexture);
    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 ) ? true : false
    
    const [overHangLeftWidth, setOverHangLeftWidth] = useState(0);
    const [overHangRightWidth, setOverHangRightWidth] = useState(0);
    const [roofLength, setRoofLength] = useState(params.p_d);
    const [regularHeight, setRegularHeight] = useState(0);
    const [isEqualLeftHeight, setIsEqualLeftHeight] = useState(false);
    const [isEqualRightHeight, setIsEqualRightHeight] = useState(false);
    const [angle, setAngle] = useState({x:0,y:0,z:0});
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(roofLength);
    const [patternPosition, setPatternPosition] = useState({x:0,y:params.p_h + (( params.p_r_p / 12)*(params.p_w/2))+0.02,z:roofLength/-2});
    
    let trimOffsetHeight = trimOffsetHeightFunc(params.p_r_p)
    
    const overHangLeftHeight = useMemo(() => {
        return params.p_r_s != 1 ? (((isEqualLeftHeight === true ? params.b_l_t_r_p : params.p_r_p) / 12) * (overHangLeftWidth - trimThickness) + trimOffsetHeight): ((params.p_r_p / 12) * overHangLeftWidth);
    }, [params.p_r_s, params.b_l_t_r_p, params.p_r_p, isEqualLeftHeight, overHangLeftWidth])

    const overHangRightHeight = useMemo(() => {
        return params.p_r_s != 1 ? (((isEqualRightHeight === true ? params.b_l_t_r_pR : params.p_r_p) / 12) * (overHangRightWidth - trimThickness) + trimOffsetHeight): ((params.p_r_p / 12) * overHangRightWidth);
    }, [params.p_r_s, params.b_l_t_r_pR, params.p_r_p, isEqualRightHeight, overHangRightWidth])

    const dynamicHeight = useMemo(() => {
        return params.singleSlope ? ((params.p_r_p / 12) * (params.p_w)) : 0;
    }, [params.singleSlope, params.p_r_p, params.p_w])

    const overhangDynamicHeightR = useMemo(() => {
        return ((params.p_r_p / 12) * overHangRightWidth)
    }, [params.singleSlope, params.p_r_p, overHangRightWidth])

    const overhangDynamicHeightL = useMemo(() => {
        return ((params.p_r_p / 12) * overHangLeftWidth)
    }, [params.singleSlope, params.p_r_p, overHangLeftWidth])

    const singleSlopeOverHang = useMemo(() => (params.singleSlope ? -overhangDynamicHeightR : overHangRightHeight), [params.singleSlope, overHangRightHeight])

    const centerRoofAngle = useMemo(() => (Math.atan2((params.p_r_p / 12), 1)), [params.p_r_p])

    const leftLeanRoofAngle = useMemo(() => {
        const leftLeanRoofPitchHeight = (params.b_l_t_r_p / 12) * params.lean_p_w
        return Math.atan((leftLeanRoofPitchHeight - (isEqualLeftHeight && trimOffsetHeight)) / (params.lean_p_w - (isEqualLeftHeight && trimThickness)));
    },[params.b_l_t_r_p, params.lean_p_w, isEqualLeftHeight, params.lean_p_w, params.add_left_front_lean_porch, params.add_left_back_lean_porch,]) 

    const rightLeanRoofAngle = useMemo(() => {
        const rightLeanRoofPitchHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;
        const angle = Math.atan((rightLeanRoofPitchHeight - (isEqualRightHeight && trimOffsetHeight)) / (params.leanR_p_w - (isEqualRightHeight && trimThickness)))
        return angle
    }, [isEqualRightHeight, params.leanR_p_w, params.b_l_t_r_pR, params.leanR_p_w])

    const roofModelOutlineLeft = useMemo(() => {
        const model = new THREE.Shape();
        const overhangDynamicHeightRight = params.singleSlope ? overhangDynamicHeightR : overHangRightHeight
        const overhangDynamicHeightLeft = params.singleSlope ? overhangDynamicHeightL : overHangLeftHeight
        if(!isEqualLeftHeight || params.singleSlope){
            model.moveTo(-params.p_w / 2 - overHangLeftWidth, 0 - overhangDynamicHeightLeft);
            model.lineTo(-params.p_w / 2 - overHangRightWidth - 0.001, 0 - overhangDynamicHeightLeft);
            model.lineTo(-params.p_w / 2 - overHangRightWidth - 0.001, roofOutlineThickness - overhangDynamicHeightLeft);
            model.lineTo(-params.p_w / 2 - overHangLeftWidth, roofOutlineThickness - overhangDynamicHeightLeft);
            model.closePath();
        }

        return model;

    }, [params.p_w, params.p_r_s, params.p_r_p, isEqualLeftHeight, regularHeight, overHangLeftWidth, overHangLeftHeight, overHangRightWidth, overHangRightHeight]);

    const roofModelOutlineRight = useMemo(() => {
        const model = new THREE.Shape();
        if(!isEqualRightHeight || params.singleSlope){
            model.moveTo(params.p_w / 2 + overHangLeftWidth, 0 - singleSlopeOverHang);
            model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, 0 - singleSlopeOverHang);
            model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, roofOutlineThickness - singleSlopeOverHang);
            model.lineTo(params.p_w / 2 + overHangLeftWidth, roofOutlineThickness - singleSlopeOverHang);
            model.closePath();
        }

        return model;

    }, [params.p_w, params.p_r_s, params.p_r_p, regularHeight, overHangLeftWidth, overHangLeftHeight, isEqualRightHeight, overHangRightWidth, overHangRightHeight]);

    const roofModelOutlineFrontBack = useMemo(() => {
        const model = new THREE.Shape();
        if((!isEqualLeftHeight && !isEqualRightHeight) || params.singleSlope){
            if(params.singleSlope && params.freeStandingSide === 'left'){
                model.moveTo(-params.p_w / 2 - overHangLeftWidth, 0 - overHangLeftHeight - dynamicHeight);
                model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, 0 - singleSlopeOverHang);
                model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, roofOutlineThickness - singleSlopeOverHang);
                model.lineTo(-params.p_w / 2 - overHangLeftWidth, roofOutlineThickness - overHangLeftHeight - dynamicHeight);
                model.closePath();
            } else {
                model.moveTo(-params.p_w / 2 - overHangLeftWidth, roofOutlineThickness - overHangLeftHeight - dynamicHeight);
                model.lineTo(0, params.p_w / 2 * params.p_r_p / 12 + 0.048);
                model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, roofOutlineThickness - singleSlopeOverHang);
                model.lineTo(params.p_w / 2 + overHangRightWidth + 0.001, 0 - singleSlopeOverHang);
                model.lineTo(0, params.p_w / 2 * params.p_r_p / 12);
                model.lineTo(-params.p_w / 2 - overHangLeftWidth, 0 - overHangLeftHeight - dynamicHeight);
                model.closePath();
            }
        }

        return model;

    }, [params.p_w, params.p_r_s, params.p_r_p, regularHeight, overHangLeftWidth, isEqualLeftHeight, isEqualRightHeight, overHangLeftHeight, overHangRightWidth, overHangRightHeight, dynamicHeight]);

    const roofInnerModel = useMemo(() => {
        const pitchHeightOffset = const_var.roofRegularDst * (params.p_r_p / 12);
        const roofSpace = 0.025;

        const leftRoofEndLength = (overHangLeftWidth - trimThickness) / Math.cos(isEqualLeftHeight ? leftLeanRoofAngle : centerRoofAngle);
        const leftRoofEndOffset = leftRoofEndLength - (overHangLeftWidth - trimThickness) / Math.cos( centerRoofAngle) ;
        const leftRoofEndPointOffset =  {
            x: Math.cos(leftLeanRoofAngle) * leftRoofEndOffset,
            y: Math.sin(leftLeanRoofAngle) * leftRoofEndOffset
        }
        const rightRoofEndLength = (overHangRightWidth - trimThickness) / Math.cos(isEqualRightHeight ? rightLeanRoofAngle : centerRoofAngle);
        const rightRoofEndOffset = rightRoofEndLength - (overHangRightWidth - trimThickness) / Math.cos( centerRoofAngle) ;
        const rightRoofEndPointOffset =  {
            x: (!params.singleSlope) ? Math.cos(rightLeanRoofAngle) * rightRoofEndOffset : 0,
            y: (!params.singleSlope) ? Math.sin(rightLeanRoofAngle) * rightRoofEndOffset : 0,
        }

        const model = new THREE.Shape();
        if (params.p_r_s === 1) {
            const availableSectionW = params.p_w - 0.07;

            model.moveTo(-(availableSectionW / 2) + roofSpace, -0.01);
            model.lineTo(-(availableSectionW / 2) + roofSpace, const_var.roofRegularDst);
            model.quadraticCurveTo(
                -(availableSectionW / 2) + roofSpace, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace,
                -(availableSectionW / 2) + roofSpace + const_var.roofRegularDst, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12 - roofSpace
            );
            model.lineTo(0, (params.p_r_p / 12) * (availableSectionW / 2) + regularHeight - roofSpace);
            model.lineTo((availableSectionW / 2) - roofSpace - const_var.roofRegularDst, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12 - roofSpace);
            model.quadraticCurveTo(
                (availableSectionW / 2) - roofSpace, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace,
                (availableSectionW / 2) - roofSpace, const_var.roofRegularDst
            );
            model.lineTo((availableSectionW / 2) - roofSpace, -0.01);
            model.lineTo((availableSectionW / 2) - roofSpace - roofThickness, -0.01);

            model.lineTo((availableSectionW / 2) - roofSpace - roofThickness, const_var.roofRegularDst - roofSpace);
            model.quadraticCurveTo(
                (availableSectionW / 2) - roofSpace - roofThickness, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace - roofThickness,
                (availableSectionW / 2) - roofSpace - const_var.roofRegularDst - roofThickness, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12 - roofSpace - roofThickness
            );
            model.lineTo(0, (params.p_r_p / 12) * (availableSectionW / 2) + regularHeight - roofSpace - roofThickness);
            model.lineTo(-((availableSectionW / 2) - roofSpace - const_var.roofRegularDst - roofThickness), const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12 - roofSpace - roofThickness)
            model.quadraticCurveTo(
                -((availableSectionW / 2) - roofSpace - roofThickness), const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace - roofThickness,
                -((availableSectionW / 2) - roofSpace - roofThickness), const_var.roofRegularDst - roofSpace
            );
            model.lineTo(-((availableSectionW / 2) - roofSpace - roofThickness), -0.01);

        } else {
            model.moveTo(-params.p_w / 2 - overHangLeftWidth + leftRoofEndPointOffset.x, 0 - overHangLeftHeight - dynamicHeight + leftRoofEndPointOffset.y);
            model.lineTo(-params.p_w / 2 - trimThickness, -trimOffsetHeight - dynamicHeight);
            if (!params.singleSlope) model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight);
            model.lineTo(params.p_w / 2 + trimThickness, (params.singleSlope ? trimOffsetHeight : -trimOffsetHeight))
            model.lineTo(params.p_w / 2 + overHangRightWidth-rightRoofEndPointOffset.x, 0 - singleSlopeOverHang + rightRoofEndPointOffset.y);
            model.lineTo(params.p_w / 2 + overHangRightWidth-rightRoofEndPointOffset.x , roofThickness - singleSlopeOverHang + rightRoofEndPointOffset.y);
            
            model.lineTo(params.p_w / 2 + trimThickness, (params.singleSlope ? trimOffsetHeight : -trimOffsetHeight) + roofThickness)
            if (!params.singleSlope) model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight + roofThickness);
            model.lineTo(-params.p_w / 2 - trimThickness, -trimOffsetHeight - dynamicHeight + roofThickness)
            model.lineTo(-params.p_w / 2 - overHangLeftWidth + leftRoofEndPointOffset.x, roofThickness - overHangLeftHeight - dynamicHeight + leftRoofEndPointOffset.y);
        }
        model.closePath();

        return model;
    }, [params.p_w, params.p_r_p, params.p_r_s, params.singleSlope, params.lean_p_h, params.leanR_p_h, params.o_v_h_n_g_e, params.o_v_h_n_g, overHangLeftWidth, overHangLeftHeight, regularHeight, overHangRightWidth, overHangRightHeight, regularHeight, isEqualLeftHeight, leftLeanRoofAngle, isEqualRightHeight, rightLeanRoofAngle, centerRoofAngle]);

    const topRidgeModel = useMemo(() => {
        const ridgeWidth = 0.25;
        
        const model = new THREE.Shape();
        model.moveTo(-ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth));

        model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight);

        model.lineTo(ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth));
        model.lineTo(ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth) + (isPattern ? ridgeThickness * 3 : ridgeThickness));

        model.lineTo(0, (params.p_r_p / 12) * params.p_w / 2 + regularHeight + (isPattern ? ridgeThickness * 3 : ridgeThickness));

        model.lineTo(-ridgeWidth, (params.p_r_p / 12) * (params.p_w / 2 - ridgeWidth) + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.closePath();

        return model;
    }, [params.p_w, params.p_r_p, regularHeight, isPattern]);

    const rightLeanRotationAngle = useMemo(() => {
        return -(rightLeanRoofAngle - centerRoofAngle)
    }, [rightLeanRoofAngle, centerRoofAngle])

    const leftLeanRotationAngle = useMemo(() => {
        return (leftLeanRoofAngle - centerRoofAngle)
    }, [leftLeanRoofAngle, centerRoofAngle])

    // create roof geometry 
    const roofComplexModel = useMemo(() => {
        // *start* left leanTo ----------------------------------------------------------------////
        const leftRoofMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern,"roof"), extrudeSettings(patternDepth)));
        const leftRoofCutMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape(),  extrudeSettings(roofLength) ))

        let leftRoofmesh = leftRoofMeshGeo.clone();
        let leftTempGroup1 = new THREE.Object3D();
        leftTempGroup1 = new THREE.Object3D();

        if(params.p_r_s == 3){
            if(params.singleSlope){
                leftRoofmesh.position.set(patternPosition.x, patternPosition.y, patternPosition.z);
                leftRoofmesh.rotation.set(angle.x,0, angle.z)
                leftRoofmesh.updateMatrixWorld();
        
                leftTempGroup1.rotation.set(0,-angle.y,0);
            }else{
                leftRoofmesh.position.set(-patternDepth,0,-patternPosition.x);
                leftRoofmesh.rotation.set(0,Math.PI/2,0)
    
                leftTempGroup1.rotation.set(0,0,angle.x);
                leftTempGroup1.position.set(0,patternPosition.y,0);
            }
        }else{
            leftRoofmesh.position.set(patternPosition.x, patternPosition.y, -patternPosition.z);
            leftRoofmesh.rotation.set(angle.x, Math.PI, angle.z)
    
            leftTempGroup1.rotation.set(0,angle.y,0);
        }
        leftTempGroup1.updateMatrixWorld();
        leftRoofmesh.updateMatrixWorld();


        // Apply the parent's world matrix to the roofmesh
        leftRoofmesh.applyMatrix4(leftTempGroup1.matrixWorld);
        leftRoofmesh.updateMatrixWorld();

        let leftRoofCutMesh = leftRoofCutMeshGeo.clone();
        leftRoofCutMesh.position.set(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        leftRoofCutMesh.rotation.set(0, 0, leftLeanRoofAngle)
        leftRoofCutMesh.visible = false
        leftRoofCutMesh.updateMatrixWorld();

        let leftSyncRoofCutMesh = leftRoofCutMeshGeo.clone();
        leftSyncRoofCutMesh.position.set(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        leftSyncRoofCutMesh.rotation.set(0, 0, (centerRoofAngle))
        leftSyncRoofCutMesh.visible = false
        leftSyncRoofCutMesh.updateMatrixWorld();

        const leftRoofMeshGeoMesh_CSG = CSG.fromMesh(leftRoofmesh);
        const leftRoofCutMeshGeoMesh_CSG = CSG.fromMesh(leftRoofCutMesh);
        const leftSyncRoofCutMeshGeoMesh_CSG = CSG.fromMesh(leftSyncRoofCutMesh);

        // cut roof geometry --------------------------------------------------------------------
        let leftRoofMeshFinalCSG;

        if (params.add_left_lean && isEqualLeftHeight) {
            if (params.p_r_s != 3) {
                leftRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG.subtract(leftRoofCutMeshGeoMesh_CSG)
            } else {
                leftRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG.subtract(leftRoofCutMeshGeoMesh_CSG)
            }
        } else {
            leftRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG
        }

        const leftEndRoofMeshFinalCSG = leftRoofMeshGeoMesh_CSG.intersect(leftSyncRoofCutMeshGeoMesh_CSG)
        const leftEndRoofMeshFinal = CSG.toMesh(leftEndRoofMeshFinalCSG, new THREE.Matrix4())
        rotateAroundCustomAxis('left', leftEndRoofMeshFinal, leftLeanRotationAngle, trimOffsetHeight);

        // *end* left leanTo -------------------------------------------------------------------////

        // *start* right leanTo ----------------------------------------------------------------////
        const rightRoofMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern,"roof"), extrudeSettings(patternDepth)));
        const rightRoofCutMeshGeo = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape(),  extrudeSettings(roofLength) ))

        let rightRoofmesh = rightRoofMeshGeo.clone();
        rightRoofmesh.position.set(patternPosition.x, patternPosition.y, patternPosition.z);
        rightRoofmesh.rotation.set(angle.x,0,angle.z)
        rightRoofmesh.updateMatrixWorld();

        let leftleftTempGroup1 = new THREE.Object3D();
        leftleftTempGroup1.rotation.set(0,angle.y,0);
        leftleftTempGroup1.updateMatrixWorld();
        
        // Apply the parent's world matrix to the roofmesh
        rightRoofmesh.applyMatrix4(leftleftTempGroup1.matrixWorld);
        rightRoofmesh.updateMatrixWorld();

        let rightRoofCutMesh = rightRoofCutMeshGeo.clone();
        rightRoofCutMesh.position.set((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        rightRoofCutMesh.rotation.set(0, 0, (Math.PI - rightLeanRoofAngle))
        rightRoofCutMesh.visible = false
        rightRoofCutMesh.updateMatrixWorld();
        
        let rightSyncRoofCutMesh = rightRoofCutMeshGeo.clone();
        rightSyncRoofCutMesh.position.set((params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (roofLength / 2))
        rightSyncRoofCutMesh.rotation.set(0, 0, (Math.PI - centerRoofAngle))
        rightSyncRoofCutMesh.visible = false
        rightSyncRoofCutMesh.updateMatrixWorld();

        const rightRoofMeshGeoMesh_CSG = CSG.fromMesh(rightRoofmesh);
        const rightRoofCutMeshGeoMesh_CSG = CSG.fromMesh(rightRoofCutMesh);
        const rightSyncRoofCutMeshGeoMesh_CSG = CSG.fromMesh(rightSyncRoofCutMesh);

        // cut roof geometry --------------------------------------------------------------------

        let rightRoofMeshFinalCSG;
        if (params.add_right_lean && isEqualRightHeight) {
            if (params.p_r_s != 3) {
                rightRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG.subtract(rightRoofCutMeshGeoMesh_CSG)
            } else {
                rightRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG.subtract(rightRoofCutMeshGeoMesh_CSG)
            }
        } else {
            rightRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG
        }
            
        // *end* right leanTo -------------------------------------------------------------------////
        
        const rightEndRoofMeshFinalCSG = rightRoofMeshGeoMesh_CSG.intersect(rightSyncRoofCutMeshGeoMesh_CSG)
        const rightEndRoofMeshFinal = CSG.toMesh(rightEndRoofMeshFinalCSG, new THREE.Matrix4())
        rotateAroundCustomAxis('right', rightEndRoofMeshFinal, rightLeanRotationAngle, trimOffsetHeight);

        const leftRoofMeshFinal = CSG.toMesh(leftRoofMeshFinalCSG, new THREE.Matrix4())
        const rightRoofMeshFinal = CSG.toMesh(rightRoofMeshFinalCSG, new THREE.Matrix4())

        return {
            leftRoofMeshFinal: leftRoofMeshFinal.geometry,
            leftEndRoofMeshFinal: leftEndRoofMeshFinal.geometry,
            rightRoofMeshFinal: rightRoofMeshFinal.geometry,
            rightEndRoofMeshFinal: rightEndRoofMeshFinal.geometry,
        }

    }, [params.p_w, params.p_h, params.add_left_lean, params.add_right_lean, params.p_r_s, angle, centerRoofAngle, patternPosition, patternLength, patternDepth, roofLength, isEqualLeftHeight, isEqualRightHeight,params.singleSlope, isPattern])

    const texture_metalSprite = useMemo(() => {
        if(const_var.metalTexture != undefined && const_var.metalTexture != ""){
            let texture =  (const_var.metalTexture != undefined) ? const_var.metalTexture.clone() : const_var.metalTexture
            textureAnisotropy(gl, texture,1.3, 1.3, (params.p_r_s === 3) ? 0 :  Math.PI/-2);    
            return texture
        }
    }, [params.p_v_w ,params.p_r_s,const_var.metalTexture])

    useEffect(() => {
        trimOffsetHeight = trimOffsetHeightFunc(params.p_r_p)
    }, [params.p_r_p]);

    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side']!=undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end']!=undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }

    useMemo(() => {
        const leanRoofLeftHeight =  (params.b_l_t_r_p / 12) * params.lean_p_w;
        const leanRoofRightHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;

        if(params.add_left_lean && params.leanStyle.left === 'standard' && (params.p_h === leanRoofLeftHeight + params.lean_p_h)) {
            setIsEqualLeftHeight(true)
        } else {
            setIsEqualLeftHeight(false)
        }
        if(params.add_right_lean && params.leanStyle.right === 'standard' && (params.p_h === leanRoofRightHeight + params.leanR_p_h)) {
            setIsEqualRightHeight(true)
        } else {
            setIsEqualRightHeight(false)
        }
    }, [params.p_h, params.lean_p_h, params.leanR_p_h, params.b_l_t_r_p, params.b_l_t_r_pR, params.add_left_lean, params.add_right_lean, params.lean_p_w, params.leanR_p_w])

    useMemo(() => {
        const diagonalLength =  Math.sqrt(Math.pow((params.p_w / 2) + overHangLeftWidth, 2) + Math.pow((params.p_r_p / 12*((params.p_w / 2) + overHangLeftWidth)), 2));

        if (params.p_r_s === 1) {
            setOverHangLeftWidth(0);
            setOverHangRightWidth(0);
            setRoofLength(params.p_d + 1.05);
            setRegularHeight(const_var.roofRegularDst * 2);

            setPatternLength(diagonalLength - overHangLeftWidth - Math.sqrt(Math.pow(const_var.roofRegularDst, 2) + Math.pow(const_var.roofRegularDst * (params.p_r_p / 12), 2)))
            setPatternDepth(roofLength)
            setAngle({x:0,y:0,z:-Math.atan2((params.p_r_p / 12), 1)})
            setPatternPosition({x:0,y:params.p_h + ((params.p_r_p / 12)*(params.p_w/2))+0.02,z:roofLength/-2})
        } else if (params.p_r_s === 2) {
            if(params.o_v_h_n_g === false){
                setOverHangRightWidth(0.75)
                setOverHangLeftWidth(0.75);
            } else {
                setOverHangLeftWidth((sideOverHangSize));
                setOverHangRightWidth((sideOverHangSize));
            }
            if(params.o_v_h_n_g_e === false) {
                setRoofLength(params.p_d + 1.5);
            } else {
                setRoofLength(params.p_d + 1 + endOverHangSize);
            }
            setRegularHeight(0);

            setPatternDepth(roofLength)
            setAngle({x: 0, y: 0, z: -Math.atan2((params.p_r_p / 12), 1)})
            
            if(params.singleSlope) {
                setPatternLength(diagonalLength * 2)
                setPatternPosition({x:params.p_w / 2 + overHangLeftWidth,y:params.p_h + overHangLeftHeight+ 0.02,z:roofLength/-2})
            } else {
                setPatternLength(diagonalLength)
                setPatternPosition({x:0,y:params.p_h + ((params.p_r_p / 12)*(params.p_w/2))+0.02,z:roofLength/-2})
            }
        } else if (params.p_r_s === 3) {
            if(params.o_v_h_n_g === false){
                setOverHangRightWidth(0.75)
                setOverHangLeftWidth(0.75);
            } else {
                setOverHangLeftWidth((sideOverHangSize));
                setOverHangRightWidth((sideOverHangSize));
            }
            if(params.o_v_h_n_g_e === false) {
                setRoofLength(params.p_d + 1.5);
            } else {
                // setRoofLength(params.p_d + endOverHangSize * 2);
                if(endOverHangSize == 0.5 ){
                    setRoofLength(params.p_d + 0.5 + endOverHangSize * 2);
                }else{
                    setRoofLength(params.p_d  + endOverHangSize * 2);
                }
            }
            setRegularHeight(0);

            setPatternLength(roofLength)
            
            setAngle({x: Math.atan2((params.p_r_p / 12), 1), y: Math.PI / 2,z: 0})
            if (params.singleSlope)  {
                setPatternDepth(diagonalLength * 2)
                setPatternPosition({x: roofLength/-2, y: params.p_h + overHangLeftHeight + 0.02, z: -(params.p_w / 2 + overHangLeftWidth)})
            } else {
                setPatternDepth(diagonalLength)
                setPatternPosition({x: roofLength / -2, y: params.p_h + ((params.p_r_p / 12) * (params.p_w / 2)) + 0.02, z: 0})
            }
        }
    }, [sideOverHangSize,endOverHangSize,params.p_r_s, params.p_w, params.p_d, params.p_h, params.p_r_p, params.singleSlope, params.b_l_t_r_p, params.b_l_t_r_pR, params.o_v_h_n_g_e, params.o_v_h_n_g, roofLength, overHangLeftWidth, overHangLeftHeight]);
    
    const texture = useMemo(() => {
        let texture = (const_var.wallTextureObj["vertical"] != undefined) ? const_var.wallTextureObj["vertical"].clone() : const_var.wallTextureObj["vertical"];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,isPattern,const_var.wallTextureObj[params.wallTexture]])

    return (
        <group onPointerOver={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} >
            {params.p_r_s === 1 &&
                <>
                    <mesh name="roof-out-left" position={[-params.p_w / 2 + const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * (params.p_r_p / 12),-patternPosition.z]} rotation={[-angle.x, 0, -angle.z]} renderOrder={1}>
                        <extrudeGeometry args={[roofPattern(patternLength, isPattern, "roof"), extrudeSettings(-patternDepth)]}/>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                    </mesh>
                    <group rotation={[0, Math.PI, 0]}>
                        <mesh name="roof-out-left" position={[-params.p_w / 2 + const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * (params.p_r_p / 12),-patternPosition.z]} rotation={[-angle.x, 0, -angle.z]} renderOrder={1}>
                            <extrudeGeometry args={[roofPattern(patternLength, isPattern, "roof"), extrudeSettings(-patternDepth)]}/>
                            <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                        </mesh>
                    </group>

                    <mesh name="roof-out-left-curve"  position={[-params.p_w / 2, params.p_h, -patternDepth/2]} rotation={[0, 0, 0]} renderOrder={1}>
                        <extrudeGeometry args={[roofPatternCurve(1, params.p_r_p,isPattern), extrudeSettings(patternDepth)]}/>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                    </mesh>
                    <mesh name="roof-out-left-curve"  position={[params.p_w / 2, params.p_h, patternDepth/2]} rotation={[0, Math.PI, 0]} renderOrder={1}>
                        <extrudeGeometry args={[roofPatternCurve(1, params.p_r_p,isPattern), extrudeSettings(patternDepth)]}/>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                    </mesh>

                    <mesh name="roof-in" position={[0, params.p_h, -roofLength / 2]}>
                        <extrudeGeometry args={[roofInnerModel, extrudeSettings(roofLength)]} />
                        <MaterialInnerRoof texture={texture_metalSprite} visibleSection={'center'} />
                    </mesh>
                </>
            }
            
            {(params.p_r_s === 2 || params.p_r_s === 3) &&
                <>
                    {!params.singleSlope &&
                        <mesh name="roof-out-right" geometry={roofComplexModel.rightRoofMeshFinal} position={[0, 0.043, 0]} renderOrder={1}>
                            <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                        </mesh>
                    }
                    {(params.add_right_lean && isEqualRightHeight && !params.singleSlope) && 
                        <mesh name="right-end-async-roof" geometry={roofComplexModel.rightEndRoofMeshFinal} position={[0, 0.043, 0]}>
                            <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                        </mesh>
                    }
                    <mesh name="roof-out-left" geometry={roofComplexModel.leftRoofMeshFinal} position={[0, 0.043, 0]} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                    </mesh>

                    {(params.add_left_lean && isEqualLeftHeight) && 
                        <mesh name="left-end-async-roof" geometry={roofComplexModel.leftEndRoofMeshFinal} position={[0, 0.043, 0]}>
                            <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"center"} />
                        </mesh>
                    }

                    <mesh name="roof-in" position={[0, params.p_h + 0.01, -roofLength / 2]}>
                        <extrudeGeometry args={[roofInnerModel, extrudeSettings(roofLength)]} />
                        <MaterialInnerRoof texture={texture_metalSprite} visibleSection={'center'} />
                    </mesh>

                    {/* Start - outline for roof sides */}
                    <>
                        <mesh name="roof-outline-right" position={[0, params.p_h + roofOutlineThickness - 0.04, -roofLength / 2]}>
                            <extrudeGeometry args={[roofModelOutlineRight, extrudeSettings(roofLength)]} />
                            <MaterialOuterRoof isPattern={isPattern} texture={null} visibleSection={'center'} />
                        </mesh>
                        <mesh name="roof-outline-left" position={[0, params.p_h + roofOutlineThickness - dynamicHeight - 0.04, -roofLength / 2]}>
                            <extrudeGeometry args={[roofModelOutlineLeft, extrudeSettings(roofLength)]} />
                            <MaterialOuterRoof isPattern={isPattern} texture={null} visibleSection={'center'} />
                        </mesh>
                        <mesh name="roof-outline-front" position={[0, params.p_h + roofOutlineThickness - 0.04, roofLength / 2]}>
                            <extrudeGeometry args={[roofModelOutlineFrontBack, extrudeSettings(0.001)]} />
                            <MaterialOuterRoof isPattern={isPattern} texture={null} visibleSection={'center'} />
                        </mesh>
                        <mesh name="roof-outline-back" position={[0, params.p_h + roofOutlineThickness - 0.04, -roofLength / 2 - 0.001]}>
                            <extrudeGeometry args={[roofModelOutlineFrontBack, extrudeSettings(0.001)]} />
                            <MaterialOuterRoof isPattern={isPattern} texture={null} visibleSection={'center'} />
                        </mesh>
                    </>
                    {/*End - outline for roof sides */}

                </>
            }
            {params.p_r_s === 3 && !params.singleSlope &&
                <mesh name="top-ridge-model" position={[0, params.p_h + 0.05 + ridgeThickness, -roofLength / 2]}>
                    <extrudeGeometry args={[topRidgeModel, extrudeSettings(roofLength)]} />
                    <MaterialRoofRidge isPattern={isPattern} visibleSection={'center'} />
                </mesh>
            }
        </group>
    );
};

export default RoofModel;
